import { SiteFooter } from "@/components/SiteFooter";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BarChart3, CalendarClock, Printer, Trophy } from "lucide-react";
import api, { planSaving } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const FEATURES = [
  [BarChart3, "Your own market prices", "Ratings turn into win chances and $ prices on a 100% market, so overlays stand out at a glance."],
  [CalendarClock, "Auto scans and late scratchings", "Re-scan the races at any time or turn on the toggle for auto scanning every 30 minutes. Keep your ratings and prices up to date with the latest scratchings and track conditions."],
  [Trophy, "Winners & places tracking", "Results come in automatically, with daily strike rates for the top pick and top 3 selections."],
  [Printer, "Print-ready race sheets", "Print a full meeting or just the races you choose, in rating or saddlecloth order."],
];

const STEPS = [["01", "Scan", "Choose states and race day, then press Scan & Rate."], ["02", "Rate", "Every horse with exposed form in every race is rated and priced."], ["03", "Bet smarter", "Open a meeting, check the form and print your selections."]];

const Cta = ({ user, testId, children }) => (
  <Link to={user ? "/program" : "/login?mode=register"} data-testid={testId}
    className="inline-flex items-center gap-2 rounded-full bg-emerald-500 hover:bg-emerald-400 text-[#060A12] font-semibold px-6 py-3 transition-colors">
    {user ? "Open Race Program" : children} <ArrowRight className="h-4 w-4" />
  </Link>
);

const Hero = ({ user }) => (
  <section className="relative overflow-hidden border-b border-slate-800" data-testid="landing-hero">
    <img src="/winx.jpg" alt="" aria-hidden className="absolute inset-0 h-full w-full object-cover opacity-25 blur-sm scale-110" />
    <div className="absolute inset-0 bg-gradient-to-r from-[#060A12] via-[#060A12]/90 to-[#060A12]/40" />
    <div className="relative max-w-6xl mx-auto px-6 py-20 sm:py-28 grid lg:grid-cols-[1.2fr_1fr] gap-12 items-center">
      <div className="fade-up">
        <div className="eyebrow text-emerald-400 mb-4">Australian thoroughbred ratings</div>
        <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold text-white leading-[0.95]" data-testid="landing-title">Strategic<br />Ratings</h1>
        <div className="mt-6 space-y-3 text-base sm:text-lg text-slate-300 max-w-xl leading-relaxed" data-testid="landing-hero-text">
          <p className="text-lg sm:text-[22px] text-white font-semibold leading-snug" data-testid="landing-story">
            <span className="text-amber-300">Established in 2000.</span> Strategic Ratings worked with an AJC handicapper, learning and studying benchmarks, classes and handicapping. We have analysed over 10,000 races using 21 matrices and backed up with seven years of first-hand stable experience makes Strategic Ratings a trusted source of racing knowledge that delivers real results.
          </p>
          <p className="text-[17px] sm:text-[20px] text-slate-100 font-medium" data-testid="landing-subheading">Strategic Ratings delivers ratings and prices for Australian horse races, <span className="text-emerald-300">7 days a week.</span></p>
          <p>We highlight our <span className="text-emerald-300 font-semibold">preferred races</span> based on exposed form and factors to deliver consistency.</p>
          <p>You can set the market percentage to suit your betting strategy and look for the value.</p>
        </div>
        <div className="mt-8 flex flex-wrap gap-3">
          <Cta user={user} testId="landing-start-trial">Start 7-day free trial</Cta>
          {!user && <Link to="/login" data-testid="landing-login" className="inline-flex items-center rounded-full border border-slate-600 hover:border-slate-400 text-white px-6 py-3 transition-colors">Log in</Link>}
        </div>
        {!user && <p className="mt-3 text-xs text-slate-500">No card needed for the trial.</p>}
      </div>
      <figure className="fade-up justify-self-center lg:justify-self-end">
        <img src="/winx.jpg" alt="Winx winning at Randwick" className="w-[340px] sm:w-[420px] aspect-[228/180] object-cover rounded-xl border border-slate-700 shadow-[0_30px_80px_rgba(0,0,0,0.6)]" data-testid="landing-photo" />
      </figure>
    </div>
  </section>
);

const Features = () => (
  <section className="max-w-6xl mx-auto px-6 py-20" data-testid="landing-features">
    <div className="eyebrow mb-3">What you get</div>
    <h2 className="font-display text-3xl sm:text-4xl font-bold text-white mb-10 max-w-2xl">Ratings built on form, not hunches.</h2>
    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-slate-800 border border-slate-800 rounded-lg overflow-hidden">
      {FEATURES.map(([Icon, t, d]) => (
        <div key={t} className="bg-[#0A0F1A] p-6 hover:bg-[#0F172A] transition-colors">
          <Icon className="h-5 w-5 text-emerald-400 mb-4" />
          <div className="text-white font-semibold mb-1.5">{t}</div>
          <p className="text-sm text-slate-400 leading-relaxed">{d}</p>
        </div>
      ))}
    </div>
  </section>
);

const Steps = () => (
  <section className="border-y border-slate-800 bg-[#0A0F1A]">
    <div className="max-w-6xl mx-auto px-6 py-16 grid sm:grid-cols-3 gap-8" data-testid="landing-steps">
      {STEPS.map(([n, t, d]) => (
        <div key={n}><div className="num text-emerald-400 text-sm mb-2">{n}</div><div className="font-display text-2xl text-white font-bold mb-1">{t}</div><p className="text-sm text-slate-400">{d}</p></div>
      ))}
    </div>
  </section>
);

const Pricing = ({ user }) => {
  const [plans, setPlans] = useState([]);
  useEffect(() => { api.get("/billing/plans").then((r) => setPlans(r.data)).catch(() => {}); }, []);
  return (
    <section className="max-w-6xl mx-auto px-6 py-20" data-testid="landing-pricing">
      <div className="eyebrow mb-3">Pricing • AUD</div>
      <h2 className="font-display text-3xl sm:text-4xl font-bold text-white mb-2">Try it free for 7 days.</h2>
      <p className="text-slate-400 mb-10">Then choose a pass. No lock-in: buying again adds the time to your current pass.</p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {plans.map((p) => { const saving = planSaving(plans, p); return (
          <div key={p.id} data-testid={`landing-plan-${p.id}`}
            className={`rounded-lg border p-5 ${saving ? "border-emerald-500 bg-emerald-500/[0.06]" : "border-slate-800 bg-[#0A0F1A]"}`}>
            <div className="eyebrow">{p.label}</div>
            <div className="num text-3xl text-white font-bold mt-2">${p.amount.toFixed(0)}</div>
            <div className="text-xs text-slate-500 mt-1">${(p.amount / p.days).toFixed(2)} per day</div>
            {saving > 0 && <div className="mt-3 text-[11px] font-mono uppercase tracking-wider text-emerald-300" data-testid={`landing-plan-best-${p.id}`}>Best value • save ${saving}</div>}
          </div>
        ); })}
      </div>
      <div className="mt-10"><Cta user={user} testId="landing-pricing-cta">Start 7-day free trial</Cta></div>
    </section>
  );
};

export default function Landing() {
  const { user } = useAuth();
  return (
    <div className="min-h-screen bg-[#060A12] text-slate-200" data-testid="landing-page">
      <header className="absolute top-0 inset-x-0 z-10">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <span className="font-display text-lg font-bold text-white">Strategic Ratings</span>
          <div className="flex items-center gap-5">
          <Link to="/contact" className="text-sm text-slate-300 hover:text-white" data-testid="landing-contact-link">Contact</Link>
          {user
            ? <Link to="/program" className="text-sm text-emerald-300 hover:text-white" data-testid="landing-open-app">Open Race Program →</Link>
            : <Link to="/login" className="text-sm text-slate-300 hover:text-white" data-testid="landing-header-login">Log in</Link>}
          </div>
        </div>
      </header>
      <Hero user={user} />
      <Features />
      <Steps />
      <Pricing user={user} />
      <SiteFooter />
    </div>
  );
}
